import {saveConnection,clearConnection,newerRefreshToken} from "./apexConnections";
const WHOOP_BASE = "https://api.prod.whoop.com/developer/v1";

export const isWhoopConnected = () => !!localStorage.getItem("whoop_refresh_token");

export function disconnectWhoop() {
  ["whoop_access_token","whoop_refresh_token","whoop_token_expiry","whoop_pending","whoop_state","apex-runs-whoop-v1"].forEach(k => localStorage.removeItem(k));
  clearConnection("whoop");
}

// Token calls always go through /.netlify/functions/whoop-token so the client secret stays on the server.
// (Run locally with `netlify dev` to get the functions.)
async function tokenExchange(params) {
  const res = await fetch("/.netlify/functions/whoop-token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  return res.json();
}

async function refreshWhoopToken(retried = false) {
  const d = await tokenExchange({
    grant_type: "refresh_token",
    refresh_token: localStorage.getItem("whoop_refresh_token"),
    redirect_uri: window.location.origin,
  });
  if (d.access_token) {
    localStorage.setItem("whoop_access_token", d.access_token);
    localStorage.setItem("whoop_refresh_token", d.refresh_token);
    localStorage.setItem("whoop_token_expiry", Date.now() + d.expires_in * 1000);
    saveConnection("whoop");
    return d.access_token;
  }
  // WHOOP rotates refresh tokens: if another device used ours, take the account's newer one once.
  const newer = !retried && await newerRefreshToken("whoop");
  if (newer) { localStorage.setItem("whoop_refresh_token", newer); return refreshWhoopToken(true); }
  throw new Error("Whoop refresh failed");
}

// One refresh at a time: WHOOP rotates refresh tokens, so parallel refreshes (four requests on
// app open) made all but the first fail, which is why WHOOP needed a manual retry every morning.
let refreshing = null;
async function whoopToken() {
  const t = localStorage.getItem("whoop_access_token");
  const exp = localStorage.getItem("whoop_token_expiry");
  if (t && exp && Date.now() < parseInt(exp) - 60000) return t;
  if (!refreshing) refreshing = refreshWhoopToken().finally(() => { refreshing = null; });
  return refreshing;
}

async function whoopGet(path) {
  const t = await whoopToken();
  const isNetlify = window.location.hostname !== "localhost";

  if (isNetlify) {
    const endpointMap = {
      "/recovery?limit=14&order=desc": "whoop-recovery",
      "/activity/sleep?limit=14&order=desc": "whoop-sleep",
      "/activity/workout?limit=20&order=desc": "whoop-workouts",
      "/cycle?limit=14&order=desc": "whoop-cycles",
    };
    const fn = endpointMap[path] || "whoop-recovery";
    const res = await fetch(`/.netlify/functions/${fn}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) throw new Error(`Whoop proxy ${res.status}`);
    return res.json();
  } else {
    const res = await fetch(`${WHOOP_BASE}${path}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) throw new Error(`Whoop ${res.status}`);
    return res.json();
  }
}

export async function exchangeWhoopCode(code) {
  const d = await tokenExchange({
    grant_type: "authorization_code",
    code,
    redirect_uri: window.location.origin,
  });
  if (d.access_token) {
    localStorage.setItem("whoop_access_token", d.access_token);
    localStorage.setItem("whoop_refresh_token", d.refresh_token);
    localStorage.setItem("whoop_token_expiry", Date.now() + d.expires_in * 1000);
    localStorage.removeItem("whoop_pending");
    localStorage.removeItem("whoop_state");
    saveConnection("whoop");
    return d;
  }
  throw new Error("Whoop exchange failed: " + JSON.stringify(d));
}

export function getWhoopAuthUrl() {
  const state = "whoop_" + Math.random().toString(36).substring(2, 15);
  localStorage.setItem("whoop_pending", "1");
  localStorage.setItem("whoop_state", state);
  const clientId = process.env.REACT_APP_WHOOP_CLIENT_ID;
  const redirectUri = encodeURIComponent(window.location.origin);
  const scope = encodeURIComponent("offline read:recovery read:sleep read:workout read:body_measurement read:cycles read:profile");
  return `https://api.prod.whoop.com/oauth/oauth2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}`;
}

export async function getWhoopData() {
  await whoopToken(); // refresh once up front, then fetch in parallel
  const [recoveries, sleeps, workouts, cycles] = await Promise.all([
    whoopGet("/recovery?limit=14&order=desc"),
    whoopGet("/activity/sleep?limit=14&order=desc"),
    whoopGet("/activity/workout?limit=20&order=desc"),
    whoopGet("/cycle?limit=14&order=desc"),
  ]);
  return { recoveries, sleeps, workouts, cycles };
}

// One page (25) of workout history, newest first; pass the previous page's next_token to go further back.
export async function getWhoopWorkoutPage(next) {
  const t = await whoopToken();
  const q = `limit=25${next ? `&nextToken=${encodeURIComponent(next)}` : ""}`;
  const url = window.location.hostname === "localhost" ? `https://api.prod.whoop.com/developer/v2/activity/workout?${q}` : `/.netlify/functions/whoop-workouts?${q}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
  if (!res.ok) throw new Error(`Whoop workouts ${res.status}`);
  return res.json();
}
